import { AsyncPipe } from '@angular/common';
import { Component, HostBinding, inject } from '@angular/core';

import { SportsCacheService } from '@frontend/sports/betting-offer/feature/sports-cache';
import { LeagueItem } from '@frontend/sports/common/core/data-access/sport-model';
import { RouterEventsService } from '@frontend/sports/common/core/utils/router-events';
import { interpolateDoubleBracesString } from '@frontend/sports/common/core/utils/string';
import { CompetitionRouteService } from '@frontend/sports/competition-list/feature';
import { ComponentProxy, ComponentProxyDirective } from '@frontend/sports/deferred/feature/component-loader';
import { CompetitionTeamsPayload } from '@frontend/sports/types/components/widget/types';
import { WidgetComponent } from '@frontend/sports/widget/core/feature';
import { WidgetHeaderComponent } from '@frontend/sports/widget/feature/header';
import { Observable, ReplaySubject, concatMap, of, switchMap, tap, withLatestFrom } from 'rxjs';

import { PruneHelperService } from '../competition-list/services/prune-helper.service';
import { VirtualCompetitionService } from '../competition-list/services/virtual-competition.service';
import { TeamPagesApiService } from '../teampages-core/team-pages-api.service';
import { TeamPagesCoreService } from '../teampages-core/team-pages-core.service';
import { TeamPagesViewModel } from '../teampages-core/team-pages.model';
import { TeamPagesCompetitionViewBinding, TeamPagesComponentLoaderService } from '../teampages/team-pages-component-loader.service';

@Component({
    selector: 'ms-competition-teams-widget',
    templateUrl: './competition-teams-widget.component.html',
    imports: [AsyncPipe, ComponentProxyDirective, WidgetHeaderComponent],
})
export class CompetitionTeamsWidgetComponent extends WidgetComponent<CompetitionTeamsPayload> {
    private loader = inject(TeamPagesComponentLoaderService);
    private teamPagesApi = inject(TeamPagesApiService);
    private teamPagesCore = inject(TeamPagesCoreService);
    private routeEvents = inject(RouterEventsService);
    private bettingCache = inject(SportsCacheService);
    private competitionRoute = inject(CompetitionRouteService);
    private pruneService = inject(PruneHelperService);
    private virtualCompetitionService = inject(VirtualCompetitionService);

    private payload = new ReplaySubject<CompetitionTeamsPayload>(1);
    private payload$ = this.payload.asObservable();

    readonly teamPagesCompetitionComponent: ComponentProxy<TeamPagesCompetitionViewBinding>;

    readonly vm$: Observable<TeamPagesViewModel | undefined>;
    title: string | undefined = undefined;

    @HostBinding('class') className = 'competition-teams-widget grid-wrapper';

    @HostBinding('class.competition-teams-widget--standalone') get standalone() {
        return !this.parent;
    }

    constructor() {
        super();

        this.teamPagesCompetitionComponent = this.loader.getTeamPagesComponent();

        this.vm$ = this.payload$.pipe(
            withLatestFrom(this.routeEvents.currentActivationEnd),
            switchMap(([payload, route]) => {
                const sport$ = this.bettingCache.getSport(this.competitionRoute.params().sport!);

                return sport$.pipe(
                    concatMap((sport) => {
                        if (!route?.snapshot) {
                            return of(undefined);
                        }

                        const { participants, competitionId } = payload;
                        const teams = participants
                            .filter((item) => item && item.participantId)
                            .map((item) => this.teamPagesApi.mapTeam(item, competitionId));

                        const leagueRoute = this.competitionRoute.params();
                        const prunedSport = this.pruneService.prune(sport, leagueRoute);
                        const competition = this.virtualCompetitionService.getCompetition(prunedSport, leagueRoute, !!leagueRoute.isVirtual);

                        this.title = this.interpolateTitle(payload.title, competition);

                        return this.teamPagesCore.getTeamPageViewModel(teams, route.snapshot);
                    }),
                );
            }),
            tap((model) => {
                if (model) {
                    this.teamPagesCompetitionComponent.update({ teampagesViewModel: model });
                }
                this.setWidgetLoaded();
            }),
        );
    }

    override onData(payload: CompetitionTeamsPayload): void {
        this.payload.next(payload);
    }

    private interpolateTitle(title: string | undefined, league: LeagueItem | undefined): string {
        return interpolateDoubleBracesString(title, { competition: league?.name ?? '' });
    }
}
